'use client';

import { useState } from 'react';
import { trackCTAClick } from '@/lib/analytics';

type Labels = {
  copyPage: string;
  copied: string;
  viewMarkdown: string;
  openInClaude: string;
  openInChatgpt: string;
  askPrompt: string;
};

const BUTTON =
  'inline-flex h-8 items-center gap-1.5 border border-black/[0.1] bg-white px-2.5 text-[13px] text-[var(--text-secondary)] transition-[background-color,border-color,color] duration-150 hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]';
const ITEM = 'block w-full rounded-md px-2.5 py-2 text-left text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]';

export default function DocsPageActions({ mdPath, pageUrl, labels }: { mdPath: string; pageUrl: string; labels: Labels }) {
  const [copied, setCopied] = useState(false);

  async function copyPage() {
    trackCTAClick({ cta_name: 'docs_copy_page', cta_location: 'docs_page', cta_destination: mdPath });
    try {
      const text = await fetch(mdPath).then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))));
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      window.open(mdPath, '_blank', 'noopener');
    }
  }

  const open = (name: 'docs_open_claude' | 'docs_open_chatgpt', base: string) => () => {
    trackCTAClick({ cta_name: name, cta_location: 'docs_page', cta_destination: pageUrl });
    const prompt = labels.askPrompt.replace('{url}', new URL(mdPath, window.location.origin).toString());
    window.open(`${base}${encodeURIComponent(prompt)}`, '_blank', 'noopener');
  };

  return (
    <div className="relative flex shrink-0 items-center" data-docs-actions>
      <button type="button" onClick={copyPage} className={`${BUTTON} rounded-l-md`}>
        <svg aria-hidden="true" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
        {copied ? labels.copied : labels.copyPage}
      </button>
      <details className="group">
        <summary className={`${BUTTON} -ml-px cursor-pointer list-none rounded-r-md px-2 [&::-webkit-details-marker]:hidden`} aria-label={labels.viewMarkdown}>
          <svg aria-hidden="true" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </summary>
        <div className="enter-pop absolute right-0 top-9 z-20 w-48 origin-top-right rounded-lg border border-black/[0.08] bg-white p-1 shadow-[var(--glass-shadow)]">
          <a href={mdPath} className={ITEM}>
            {labels.viewMarkdown}
          </a>
          <button type="button" onClick={open('docs_open_claude', 'https://claude.ai/new?q=')} className={ITEM}>
            {labels.openInClaude}
          </button>
          <button type="button" onClick={open('docs_open_chatgpt', 'https://chatgpt.com/?q=')} className={ITEM}>
            {labels.openInChatgpt}
          </button>
        </div>
      </details>
    </div>
  );
}
